// Admin Audit Log — owner-only view of every write-scope admin action.
//
// Feeds the future Hubstaff-basic analytics module; for now shows a
// reverse-chronological list with quick filters by action + user.
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, ScrollText } from "lucide-react";
import { workspaceApi } from "./_shared";
import "./workspace.css";

const ACTION_LABEL = {
  task_export:          { label: "Export CSV",     tone: "text-sky-300"     },
  task_import:          { label: "Import CSV",     tone: "text-fuchsia-300" },
  org_chart_patch:      { label: "Org Chart Edit", tone: "text-emerald-300" },
  task_bulk_patch:      { label: "Bulk Edit",      tone: "text-teal-300"    },
  task_bulk_delete:     { label: "Bulk Delete",    tone: "text-rose-300"    },
  access_user_change:   { label: "Access · User",   tone: "text-orange-300"  },
  access_preset_change: { label: "Access · Preset", tone: "text-amber-300"   },
};

function fmtTs(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function AdminAuditLog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("");
  const [userId, setUserId] = useState("");
  const [users, setUsers] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [logs, usersR] = await Promise.all([
        workspaceApi.adminLog({ ...(action && { action }), ...(userId && { user_id: userId }), limit: 500 }),
        workspaceApi.listUsers(),
      ]);
      setItems(logs.items || []);
      setUsers(usersR || []);
    } catch (e) {
      toast.error("Failed to load audit log", { description: e?.response?.data?.detail || "" });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [action, userId]);

  const userMap = useMemo(() => Object.fromEntries(users.map(u => [u.id, u])), [users]);

  return (
    <div className="space-y-4" data-testid="workspace-audit">
      <div className="flex items-center gap-2 flex-wrap">
        <ScrollText className="w-4 h-4 text-rose-300" />
        <div className="text-[13px] font-semibold text-white flex-1">Admin Activity Log</div>
        <select className="ws-input max-w-[180px]" value={action} onChange={(e) => setAction(e.target.value)} data-testid="ws-audit-action">
          <option value="">All actions</option>
          <option value="task_export">Export CSV</option>
          <option value="task_import">Import CSV</option>
          <option value="org_chart_patch">Org Chart Edit</option>
          <option value="task_bulk_patch">Bulk Edit</option>
          <option value="task_bulk_delete">Bulk Delete</option>
          <option value="access_user_change">Access · User</option>
          <option value="access_preset_change">Access · Preset</option>
        </select>
        <select className="ws-input max-w-[220px]" value={userId} onChange={(e) => setUserId(e.target.value)} data-testid="ws-audit-user">
          <option value="">All users</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
        </select>
        <button className="text-slate-400 hover:text-white p-1.5" onClick={load} title="Refresh" data-testid="ws-audit-refresh">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="ws-card p-0" data-testid="ws-audit-list">
        <div className="grid grid-cols-[140px_140px_180px_1fr] gap-3 px-3 py-2 text-[10px] font-mono text-slate-500 uppercase tracking-widest border-b border-white/5 sticky top-0 bg-white/[0.015]">
          <div>Time</div><div>Action</div><div>User</div><div>Details</div>
        </div>
        {loading && <div className="text-center text-slate-500 py-8">Loading…</div>}
        {!loading && items.length === 0 && <div className="text-center text-slate-500 py-8 text-[13px]">No entries yet.</div>}
        {!loading && items.map((row) => {
          const meta = ACTION_LABEL[row.action] || { label: row.action, tone: "text-slate-300" };
          const who = userMap[row.user_id]?.name || row.user_email || row.user_id?.slice(0, 8) || "—";
          return (
            <div key={row.id} className="grid grid-cols-[140px_140px_180px_1fr] gap-3 px-3 py-2 border-b border-white/[0.03] text-[12px]" data-testid={`ws-audit-row-${row.id}`}>
              <div className="text-slate-400 font-mono text-[11px]">{fmtTs(row.ts)}</div>
              <div className={`${meta.tone} font-mono text-[11px] uppercase tracking-widest`}>{meta.label}</div>
              <div className="text-slate-300 truncate" title={row.user_email}>{who}</div>
              <div className="text-slate-400 font-mono text-[11px] truncate" title={JSON.stringify(row.meta)}>
                {Object.entries(row.meta || {}).map(([k, v]) => (
                  <span key={k} className="mr-2"><span className="text-slate-500">{k}=</span>{typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
